"use client";

import { cn } from "@/lib/utils";
import React, { useState } from "react";
import { ButtonStyleType, SpacingType } from "@/types";
import { sectionVariants } from "@/types";
import { Button } from "@/components/ui/button";
import Image from '@/components/ui/Image';
import Link from "next/link";
import { VideoIframe, useVideo } from "@/components/components/VideoIframe";
import parse from "html-react-parser";
import { SectionWrapper } from "../components/SectionWraper";
import { Badge } from "@/components/ui/badge";

interface Feature25Props { 
  darkMode?: boolean; 
  variant?: sectionVariants;
  spacing?: SpacingType;
  usedInPageNav?: boolean;
  pageNavTitle?: string;
  themeChangeOnScroll?: boolean;
  mediaPosition?: "left" | "right";
  mediaPositionMobile?: "top" | "bottom";
  removePaddingTop?: boolean;
  removePaddingBottom?: boolean;
  badge?: string;
  title?: string; 
  content?: string;
  hasButton?: boolean;
  buttonText?: string;
  buttonLink?: string;
  buttonStyle?: ButtonStyleType;
  microCopy?: string;
  secondaryButton?: boolean;
  secondaryButtonText?: string;
  secondaryButtonLink?: string;
  secondaryButtonStyle?: ButtonStyleType;
  featuredItem?: "video" | "image";
  withBorder?: boolean;
  video?: string;
  loopVideo?: string;
  image?: string; 
  imageAltText?: string;  
  attributes?: React.HTMLProps<HTMLElement>;
}

const Feature25 = ({
  darkMode = false,
  variant = "A",
  spacing,
  usedInPageNav = false,
  pageNavTitle = "Feature 2",
  mediaPosition,
  mediaPositionMobile,
  themeChangeOnScroll = false,
  removePaddingTop = false,
  removePaddingBottom = false,
  badge,
  title,
  content,
  hasButton,
  buttonText,
  buttonLink,
  buttonStyle,
  microCopy,
  secondaryButton,
  secondaryButtonText,
  secondaryButtonLink,
  secondaryButtonStyle,
  featuredItem,
  withBorder = false,
  video,
  loopVideo,
  image,  
  imageAltText, 
  attributes,
}: Feature25Props) => {  
  const { isVideoOpen, currentVideo, getVideoThumbnail, handleVideoClick, handleCloseVideo } = useVideo();
  const [videoError, setVideoError] = useState(false);

  const getVideoType = (url: string) => {
    const extension = url.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'mp4':
        return 'video/mp4';
      case 'webm':
        return 'video/webm';
      case 'ogg':
        return 'video/ogg';
      case 'mov':
        return 'video/quicktime';
      case 'avi':
        return 'video/x-msvideo';
      default:
        return 'video/mp4'; // Default fallback
    }
  };

  // Desktop: media left/right. Mobile: media top/bottom via flex-col order.
  const mediaOrder = cn(
    mediaPositionMobile === "top" ? "order-first" : "order-last",
    mediaPosition === "left" ? "lg:order-first" : "lg:order-last",
  );
  const contentOrder = cn(
    mediaPositionMobile === "top" ? "order-last" : "order-first",
    mediaPosition === "left" ? "lg:order-last" : "lg:order-first",
  );

  return (
    <SectionWrapper
      usedInPageNav={usedInPageNav}
      pageNavTitle={pageNavTitle}
      themeChangeOnScroll={themeChangeOnScroll}
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={false}
      componentName="feature25"
      attributes={attributes}
      removePaddingTop={removePaddingTop}
      removePaddingBottom={removePaddingBottom}
      className="flex flex-col lg:flex-row lg:h-svh lg:max-h-[1000px] overflow-hidden"
    >
      {/* Media half — aspect-video on mobile, fills full height on desktop */}
      <div className={cn(
        "relative w-full aspect-video lg:aspect-auto lg:w-1/2 lg:h-full",
        mediaOrder,
      )}>
        {featuredItem === "video" && video && (
          <div
            className="relative w-full h-full cursor-pointer group"
            onClick={() => handleVideoClick(video)}
          >
            {loopVideo && (
              <>
                <video
                  preload="none"
                  autoPlay
                  loop
                  muted
                  playsInline
                  className={cn("w-full h-full object-cover", withBorder && "border border-border", videoError && "hidden")}
                  onError={() => setVideoError(true)}
                >
                  <source src={loopVideo} type={getVideoType(loopVideo)} />
                </video>
                <Image
                  src={image || "/placeholder.svg"}
                  alt={imageAltText || ""}
                  title={imageAltText || ""}
                  fill
                  className={cn("object-cover", withBorder && "border border-border", !videoError && "hidden")}
                />
              </>
            )}
            {!loopVideo && image && (
              <Image
                src={image || getVideoThumbnail(video) || ""}
                alt={imageAltText || ""}
                title={imageAltText || ""}
                fill
                className={cn("object-cover", withBorder && "border border-border")}
              />
            )}
            <Button
              variant="secondary"
              size="iconLg"
              className="absolute bottom-6 left-6 bg-muted rounded-full"
            >
              <span className="material-symbols-outlined font-variation-filled">play_arrow</span>
            </Button>
            <VideoIframe
              videoUrl={currentVideo}
              isOpen={isVideoOpen}
              onClose={handleCloseVideo}
            />
          </div>
        )}
        {featuredItem === "image" && image && (
          <Image
            src={image}
            alt={imageAltText ?? ""}
            title={imageAltText ?? ""}
            fill
            className={cn("object-cover", withBorder && "border border-border")}
          />
        )}
      </div>

      {/* Content half */}
      <div className={cn(
        "flex w-full items-center justify-center lg:w-1/2",
        contentOrder,
      )}>
        <div className="flex flex-col gap-8 px-8 py-12 lg:px-16 max-w-[560px] w-full" data-aos="fade-in" data-aos-duration="1000">
          {badge && (
            <Badge variant="outline" bold>
              {badge}
            </Badge>
          )}
          {title && <h2>{parse(title)}</h2>}
          {content && (
            <div className="text-muted-foreground paragraph-lg">
              {parse(content)}
            </div>
          )}
          {hasButton && (
            <div className="flex flex-col gap-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <Button variant={buttonStyle} size="lg" asChild className="w-full sm:w-auto">
                  <Link href={buttonLink ?? ""}>{buttonText}</Link>
                </Button>
                {secondaryButton && (
                  <Button variant={secondaryButtonStyle} size="lg" asChild className="w-full sm:w-auto">
                    <Link href={secondaryButtonLink ?? ""}>{secondaryButtonText}</Link>
                  </Button>
                )}
              </div>
              {microCopy && (
                <div className="text-muted-foreground paragraph-sm">
                  {parse(microCopy)}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </SectionWrapper>
  );
};

export { Feature25 };



